import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { BulkVoucherResponse } from '@/services/admin/vouchers/vouchers.interfaces';
import { Users, Calendar, Trash2, Play, Pause, Download } from 'lucide-react';

interface VoucherBulkActionsProps {
  selectedIds: string[];
  onBulkActivate: (ids: string[]) => Promise<BulkVoucherResponse>;
  onBulkDeactivate: (ids: string[]) => Promise<BulkVoucherResponse>;
  onBulkDelete: (ids: string[]) => Promise<BulkVoucherResponse>;
  onBulkExtendValidity: (ids: string[], newValidUntil: string) => Promise<BulkVoucherResponse>;
  onExport: (ids: string[]) => void;
  isLoading?: boolean;
}

export const VoucherBulkActions: React.FC<VoucherBulkActionsProps> = ({
  selectedIds,
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
  onBulkExtendValidity,
  onExport,
  isLoading = false,
}) => {
  const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newValidUntil, setNewValidUntil] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const hasSelection = selectedIds.length > 0;

  const handleBulkAction = async (
    action: string,
    actionFn: () => Promise<BulkVoucherResponse>
  ) => {
    if (!hasSelection) return;

    setActionLoading(action);
    try {
      const result = await actionFn();
      
      // Mostrar resultado
      if (result.success.length > 0) {
        console.log(`✅ ${result.success.length} vouchers processados com sucesso`);
      }
      if (result.failed.length > 0) {
        console.log(`❌ ${result.failed.length} vouchers falharam`);
        result.failed.forEach(failure => {
          console.log(`  - ${failure.id}: ${failure.error}`);
        });
      }
    } catch (error) {
      console.error('Erro na ação em lote:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = () => {
    handleBulkAction('activate', () => onBulkActivate(selectedIds));
  };

  const handleDeactivate = () => {
    handleBulkAction('deactivate', () => onBulkDeactivate(selectedIds));
  };

  const handleDelete = () => {
    handleBulkAction('delete', () => onBulkDelete(selectedIds));
    setIsDeleteDialogOpen(false);
  };

  const handleExtendValidity = () => {
    if (!newValidUntil) return;
    
    handleBulkAction('extend', () => onBulkExtendValidity(selectedIds, newValidUntil));
    setIsExtendDialogOpen(false);
    setNewValidUntil('');
  };

  const handleExport = () => {
    onExport(selectedIds);
  };

  if (!hasSelection) {
    return null;
  }

  return (
    <Card className="mb-6 border-blue-200 bg-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5" />
          Ações em Lote ({selectedIds.length} selecionado{selectedIds.length > 1 ? 's' : ''})
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleActivate}
            disabled={isLoading || actionLoading !== null}
            className="flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            Ativar
            {actionLoading === 'activate' && <span className="ml-1">...</span>}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleDeactivate}
            disabled={isLoading || actionLoading !== null}
            className="flex items-center gap-2"
          >
            <Pause className="w-4 h-4" />
            Desativar
            {actionLoading === 'deactivate' && <span className="ml-1">...</span>}
          </Button>

          <Dialog open={isExtendDialogOpen} onOpenChange={setIsExtendDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isLoading || actionLoading !== null}
                className="flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Estender Validade
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Estender Validade</DialogTitle>
                <DialogDescription>
                  Defina uma nova data de validade para {selectedIds.length} voucher{selectedIds.length > 1 ? 's' : ''} selecionado{selectedIds.length > 1 ? 's' : ''}.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nova Data de Validade
                  </label>
                  <Input
                    type="datetime-local"
                    value={newValidUntil}
                    onChange={(e) => setNewValidUntil(e.target.value)}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsExtendDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleExtendValidity}
                  disabled={!newValidUntil || actionLoading !== null}
                >
                  {actionLoading === 'extend' ? 'Processando...' : 'Confirmar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar
          </Button>

          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={isLoading || actionLoading !== null}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogDescription>
                  Tem certeza que deseja excluir {selectedIds.length} voucher{selectedIds.length > 1 ? 's' : ''}? 
                  Esta ação não pode ser desfeita.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === 'delete' ? 'Excluindo...' : 'Excluir'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};
